import moment from 'moment-timezone';
import React, { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { CampaignContext } from '../ProactiveCommunications';
import campaignsResource, { CampaignType } from 'src/actions/campaigns/campaignsResource';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import { ControlLabel } from 'src/presentation/components/agent_desktop_components';
import { FIELDS_MAPPER } from 'src/presentation/components/pages/campaigns/campaignsConstants';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import HoursTableSection from 'src/presentation/components/pages/shared/HoursTable/HoursTable';
import Input from 'src/presentation/components/forms/InputField';
import { STATUS_PERSISTING, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';
import TextareaField from 'src/presentation/components/forms/TextareaField';
import useDirtyFormConfirmation from 'src/presentation/components/pages/shared/hooks/useDirtyFormConfirmation';

const CampaignsEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const dispatch = useDispatch();
  const { campaignType, contentEditor } = useContext(CampaignContext);
  const { persistingState } = useResourceValues(campaignsResource);

  const { add, update, validate } = campaignsResource.effects;
  const { resetErrors } = campaignsResource.actions;

  const formContext = useForm({
    add,
    defaultItem: {
      name: '',
      description: '',
      type: campaignType,
      content: { answerLanguage: 'en-us' },
      version: 1,
      officeHours: {
        timezone: moment.tz.guess(),
        friday: [{ start: '09:00', stop: '17:00' }],
        thursday: [{ start: '09:00', stop: '17:00' }],
        wednesday: [{ start: '09:00', stop: '17:00' }],
        tuesday: [{ start: '09:00', stop: '17:00' }],
        monday: [{ start: '09:00', stop: '17:00' }],
        sunday: [],
        saturday: [],
        exceptions: [],
      },
    },
    errors,
    item,
    resetErrors,
    root,
    update,
    validate,
  });
  const { onClose, onSave, isNew, workingStateRef } = formContext;
  const [isComplete, setComplete] = useState<boolean>();

  const { handleCancel, handleStay, isConfirmCancelModalVisible, isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  const handleOnSave = () => {
    if (!dispatch(validate(workingStateRef.current))) {
      return;
    }
    onSave();
  };

  useEffect(() => {
    if (persistingState === STATUS_SUCCEEDED) {
      setComplete(true);
    }
  }, [persistingState, setComplete]);

  return (
    <>
      <Form context={formContext}>
        <H1 data-aid="campaigns-editor-h1">
          {isNew ? 'Create' : 'Edit'} {CAMPAIGN_LABELS[campaignType].title} Campaign
        </H1>

        <Body data-aid="campaigns-editor">
          <Section>
            <Row>
              <Input
                data-aid="campaigns-name-input"
                description={
                  <>
                    We recommend the name includes when the Campaign will run. <br />
                    For example, Q3 Holiday 2025.
                  </>
                }
                attr="name"
                autoFocus
                label="Campaign name"
                placeholder="Enter the name"
              />
            </Row>
            <Row>
              <DescriptionTextarea
                data-aid="campaigns-description-textarea"
                attr="description"
                label="Description"
                placeholder="Describe the Campaign"
              />
            </Row>

            {contentEditor}

            <ScheduledRow data-aid="campaigns-scheduler">
              <ControlLabel data-aid="campaigns-scheduler-title">Campaign schedule</ControlLabel>
              <HoursTableSection
                description={
                  <OfficeHoursDescription>{CAMPAIGN_LABELS[campaignType].scheduleDescription}</OfficeHoursDescription>
                }
                header=""
                showTimezone={false}
                dayPath={FIELDS_MAPPER.HOURS.path}
                timezonePath={FIELDS_MAPPER.TIMEZONE.path}
              />
            </ScheduledRow>
          </Section>
        </Body>

        <Footer>
          <Button data-aid="campaign-edit-cancel" type={Button.Types.TEXT} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            data-aid="campaign-edit-save"
            onClick={handleOnSave}
            isLoading={persistingState === STATUS_PERSISTING}
            disabled={!isModified}
          >
            {isNew ? 'Create Campaign' : 'Save'}
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Campaign"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
};

const ScheduledRow = styled(Row)`
  flex-wrap: wrap;
`;

const DescriptionTextarea = styled(TextareaField)`
  min-height: 60px;
`;

const OfficeHoursDescription = styled.div`
  max-width: 470px;
`;

export default CampaignsEditor;

const CAMPAIGN_LABELS = {
  [CampaignType.SMS]: {
    title: 'an SMS',
    scheduleDescription:
      "When would you like to send the SMS messages? Schedule based on your recipients' local timezone, and we will send up to 40 messages every 5 minutes.",
  },
  [CampaignType.EMAIL]: {
    title: 'an Email',
    scheduleDescription:
      "When would you like to send the emails? Schedule based on your recipients' local timezone, and we will send up to 40 emails every 5 minutes.",
  },
  [CampaignType.VOICE]: {
    title: 'a Voice',
    scheduleDescription:
      "When would you like to start the outgoing calls? Schedule based on your recipients' local timezone, and the calls will be placed within these hours.",
  },
};
